/* eslint-disable react-refresh/only-export-components -- pipeline module:
   CaptureRunner is internal (never exported); the exports are the hook and
   job helpers, so there is no fast-refresh boundary to preserve. */
import React, { useEffect, useMemo, useRef, useState } from "react";

import { CompareSide, StoryScene } from "../../../types/storyMap";
import StoryMapCanvas, { StoryMapCanvasHandle } from "../StoryMapCanvas";
import {
  StorySceneSources,
  assetsTilesByProject,
  useCompareSideLayers,
  useSceneLayerStack,
} from "../useStorySceneLayers";
import { useFinalFrameLayers } from "./useFinalFrameLayers";
import StoryFuel3DLayers from "../StoryFuel3DLayers";
import { LegendRow, mergeLegendRows } from "../storyLegend";
import { WeatherPanel } from "../useEntryLayerStack";

/** Crisp-print capture size (~16:10, doubled again by devicePixelRatio). */
const CAPTURE_WIDTH = 1400;
const CAPTURE_HEIGHT = 875;
/** Max wait for the map to go idle after mount before capturing anyway. */
const IDLE_TIMEOUT_MS = 8000;
/**
 * Extra wait allowed for 3D fuel stands: planting runs in a worker after the
 * map first idles, and a large stand can take several seconds.
 */
const FUEL_3D_TIMEOUT_MS = 20000;
/** Max wait for a job's layer data (queries/tile resolves) to settle. */
const DATA_TIMEOUT_MS = 12000;
/**
 * While animation data is still actively downloading the timer above is
 * deferred — frozen confidence stacks reach ~200 MB per scene, far beyond 12 s
 * on most links, and capturing without them printed base maps with no fire.
 * This is the absolute ceiling for that wait.
 */
const DATA_FETCHING_TIMEOUT_MS = 10 * 60 * 1000;

/** Key of a compare side's capture within the results map. */
export const sideCaptureKey = (
  sceneId: string,
  side: "before" | "after"
): string => `${sceneId}::${side}`;

export interface CaptureJob {
  /** Result key: scene.id, or sideCaptureKey() for compare sides. */
  key: string;
  scene: StoryScene;
  /** Set for compare-side jobs; absent for whole-scene jobs. */
  side?: CompareSide;
  sidePrefix?: string;
}

/** Flatten scenes into sequential capture jobs (compare = one per side). */
export function buildCaptureJobs(scenes: StoryScene[]): CaptureJob[] {
  const jobs: CaptureJob[] = [];
  scenes.forEach((scene) => {
    if (scene.mode === "compare" && scene.compare) {
      jobs.push({
        key: sideCaptureKey(scene.id, "before"),
        scene,
        side: scene.compare.before,
        sidePrefix: "before",
      });
      jobs.push({
        key: sideCaptureKey(scene.id, "after"),
        scene,
        side: scene.compare.after,
        sidePrefix: "after",
      });
    } else {
      jobs.push({ key: scene.id, scene });
    }
  });
  return jobs;
}

const EMPTY_SIDE: CompareSide = { label: "", layers: [] };

interface CaptureRunnerProps {
  job: CaptureJob;
  sources: StorySceneSources;
  isPublic: boolean;
  onDone: (
    key: string,
    dataUrl: string | null,
    legendRows: LegendRow[],
    weatherPanel: WeatherPanel | null
  ) => void;
}

/**
 * Mounts ONE hidden fixed-size StoryMapCanvas for the current job (WebGL
 * context limits forbid parallel maps), waits for its data + tiles, captures
 * a PNG data URL and reports back. Sequenced by useSceneCaptures via `key`.
 *
 * Timeline: (1) wait for the job's layer-data queries to settle (or
 * DATA_TIMEOUT_MS), only then mount the map so its layer list is final;
 * (2) wait for the map's "idle" event — it fires once style, sources and
 * tiles are fully loaded — with an IDLE_TIMEOUT_MS fallback; (3) wait one
 * animation frame so the last render lands in the preserved drawing buffer,
 * then getCanvas().toDataURL("image/png"). Any failure resolves null and the
 * document shows a placeholder instead.
 */
function CaptureRunner({
  job,
  sources,
  isPublic,
  onDone,
}: CaptureRunnerProps) {
  const isSide = !!job.side;
  const isAnimation = !isSide && job.scene.mode === "animation";
  const canvasRef = useRef<StoryMapCanvasHandle | null>(null);

  // Hooks run unconditionally; the branches not relevant to this job are
  // disabled/empty and settle immediately.
  const sceneStack = useSceneLayerStack({
    scene: isSide ? undefined : job.scene,
    sources,
    isPublic,
    idPrefix: "print-scene",
  });
  const sideStack = useCompareSideLayers(
    job.side ?? EMPTY_SIDE,
    job.sidePrefix === "after" ? "after" : "before",
    `print-${job.sidePrefix ?? "side"}`,
    isPublic,
    sources
  );
  const finalAnim = useFinalFrameLayers({
    animations: isAnimation ? job.scene.animations : null,
    defaultProjectId: sources.defaultProjectId,
    isPublic,
    enabled: isAnimation,
    assetsTilesUrl: sources.assetsTilesUrl,
    assetsTilesUrlByProject: assetsTilesByProject(sources),
    getMap: () => canvasRef.current?.getMap() ?? null,
  });

  const layers = isSide
    ? sideStack.layers
    : isAnimation
    ? [...sceneStack.layers, ...finalAnim.layers]
    : sceneStack.layers;
  const settled = isSide
    ? sideStack.settled
    : sceneStack.settled && (!isAnimation || finalAnim.settled);
  const legendRows = isSide ? sideStack.legendRows : sceneStack.legendRows;
  const legendRowsRef = useRef(legendRows);
  legendRowsRef.current = legendRows;
  const weatherPanel = isSide ? sideStack.weatherPanel : sceneStack.weatherPanel;
  const weatherPanelRef = useRef(weatherPanel);
  weatherPanelRef.current = weatherPanel;
  // 3D fuel stands render through their own components (not the layer list),
  // so the capture also waits until each has completed a planting pass.
  const fuel3d = isSide ? sideStack.fuel3d : sceneStack.fuel3d;
  const fuel3dRef = useRef(fuel3d);
  fuel3dRef.current = fuel3d;
  const [captureMap, setCaptureMap] = useState<import("mapbox-gl").Map | null>(null);
  const plantedRef = useRef(new Set<string>());
  const onPlanted = (itemId: string, complete: boolean) => {
    if (complete) plantedRef.current.add(itemId);
  };

  // Data-settle timeout: never let a hung query stall the whole document —
  // but a download that is visibly still progressing is not hung, so keep
  // waiting for it (up to DATA_FETCHING_TIMEOUT_MS) rather than capturing a
  // map with the fire missing.
  const [dataTimedOut, setDataTimedOut] = useState(false);
  const fetchingRef = useRef(false);
  fetchingRef.current = isAnimation && finalAnim.fetching;
  useEffect(() => {
    const startedAt = Date.now();
    let timer: ReturnType<typeof setTimeout>;
    const check = () => {
      const stillDownloading =
        fetchingRef.current &&
        Date.now() - startedAt < DATA_FETCHING_TIMEOUT_MS;
      if (stillDownloading) {
        timer = setTimeout(check, 1000);
        return;
      }
      setDataTimedOut(true);
    };
    timer = setTimeout(check, DATA_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);
  // Latched: once the map mounts it must stay mounted until the capture ends,
  // even if a query identity blip momentarily flips `settled` back.
  const mountedOnceRef = useRef(false);
  if (settled || dataTimedOut) mountedOnceRef.current = true;
  const mountMap = mountedOnceRef.current;

  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const layersRef = useRef(layers);
  layersRef.current = layers;

  useEffect(() => {
    if (!mountMap) return;
    let finished = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let idleMap: import("mapbox-gl").Map | null = null;
    let onIdle: (() => void) | null = null;

    const finish = (dataUrl: string | null) => {
      if (finished) return;
      finished = true;
      onDoneRef.current(job.key, dataUrl, legendRowsRef.current, weatherPanelRef.current);
    };
    const capture = () => {
      const map = canvasRef.current?.getMap();
      if (!map) {
        finish(null);
        return;
      }
      // One frame so the latest render (incl. the animation canvases) is in
      // the preserved drawing buffer before reading it back.
      requestAnimationFrame(() => {
        try {
          finish(map.getCanvas().toDataURL("image/png"));
        } catch {
          finish(null);
        }
      });
    };

    // Hard fallback: capture whatever is on screen after IDLE_TIMEOUT_MS
    // (longer when 3D stands still have to plant).
    const timeout = setTimeout(
      capture,
      fuel3dRef.current.length > 0 ? IDLE_TIMEOUT_MS + FUEL_3D_TIMEOUT_MS : IDLE_TIMEOUT_MS
    );
    // Every 3D stand has planted at least once (none = trivially true).
    const standsReady = () =>
      fuel3dRef.current.every((item) => plantedRef.current.has(item.id));
    // Once idle with every stand planted, give the planted frame a paint
    // before reading the buffer back.
    const captureWhenStandsReady = (map: import("mapbox-gl").Map) => {
      if (standsReady()) {
        capture();
        return;
      }
      const onIdleAgain = () => {
        if (!map.areTilesLoaded() || !standsReady()) return;
        map.off("idle", onIdleAgain);
        capture();
      };
      idleMap = map;
      onIdle = onIdleAgain;
      map.on("idle", onIdleAgain);
      // A plant that finishes while the map is already idle repaints the map
      // (triggerRepaint), which brings another "idle" to catch it.
    };

    // The map is created in StoryMapCanvas's mount effect, and the declared
    // layers are attached by its reconcile effect one commit after "load" —
    // so poll until the map exists AND every declared source is on the map,
    // THEN wait for "idle" (fires once all sources' tiles/data finish
    // loading). Guards against idling early on the bare base style.
    pollTimer = setInterval(() => {
      const map = canvasRef.current?.getMap();
      if (!map || finished) return;
      const sourcesAttached = layersRef.current.every(
        (l) => !!map.getSource(l.id)
      );
      if (!map.isStyleLoaded() || !sourcesAttached) return;
      if (pollTimer) clearInterval(pollTimer);
      pollTimer = null;
      if (map.loaded() && map.areTilesLoaded()) {
        captureWhenStandsReady(map);
        return;
      }
      idleMap = map;
      onIdle = () => {
        if (!map.areTilesLoaded()) return; // idle during a later load burst
        map.off("idle", onIdle!);
        onIdle = null;
        captureWhenStandsReady(map);
      };
      map.on("idle", onIdle);
    }, 100);

    return () => {
      finished = true;
      clearTimeout(timeout);
      if (pollTimer) clearInterval(pollTimer);
      if (idleMap && onIdle) idleMap.off("idle", onIdle);
    };
  }, [mountMap, job.key]);

  // Off-viewport but NOT display:none — mapbox needs real layout to render.
  return (
    <div
      aria-hidden
      style={{
        position: "fixed",
        top: 0,
        left: -(CAPTURE_WIDTH + 200),
        width: CAPTURE_WIDTH,
        height: CAPTURE_HEIGHT,
        overflow: "hidden",
        pointerEvents: "none",
      }}
    >
      {mountMap && (
        <>
          <StoryMapCanvas
            ref={canvasRef}
            layers={layers}
            initialCamera={job.scene.camera}
            initialBounds={sources.boundaryFeature}
            interactive={false}
            preserveDrawingBuffer
            onMapReady={setCaptureMap}
          />
          <StoryFuel3DLayers
            map={captureMap}
            items={fuel3d}
            crownsEnabled={!isPublic}
            onPlanted={onPlanted}
          />
        </>
      )}
    </div>
  );
}

export interface SceneCapturesState {
  /** key (scene.id / sideCaptureKey) → PNG data URL, or null on failure. */
  captures: Record<string, string | null>;
  /** scene.id → legend rows for the captured layers (compare = both sides). */
  legends: Record<string, LegendRow[]>;
  /** scene.id → weather readings shown beside the captured map, if any. */
  weatherPanels: Record<string, WeatherPanel>;
  /** 1-based index of the map being prepared (== total when done). */
  current: number;
  total: number;
  done: boolean;
  /** Render this while !done — the hidden capture map. */
  runner: React.ReactNode;
  /**
   * Rough seconds remaining, from the average of the jobs done so far; null
   * until the first one lands. A long story is 20+ sequential captures, so a
   * bare spinner can sit there for minutes with nothing to read.
   */
  secondsRemaining: number | null;
  /** Give up on the remaining maps; captured ones are kept, the rest are blank. */
  cancel: () => void;
  cancelled: boolean;
}

/**
 * Sequential map-capture pipeline for the print document: one CaptureRunner
 * at a time, advancing as each job reports its data URL (or null failure).
 */
export function useSceneCaptures({
  scenes,
  sources,
  isPublic,
  enabled,
}: {
  scenes: StoryScene[];
  sources: StorySceneSources;
  isPublic: boolean;
  /** False until the story content has loaded. */
  enabled: boolean;
}): SceneCapturesState {
  const jobs = useMemo(() => buildCaptureJobs(scenes), [scenes]);
  const [index, setIndex] = useState(0);
  const [captures, setCaptures] = useState<Record<string, string | null>>({});
  const [legends, setLegends] = useState<Record<string, LegendRow[]>>({});
  const [weatherPanels, setWeatherPanels] = useState<Record<string, WeatherPanel>>({});
  const [cancelled, setCancelled] = useState(false);
  const startedAtRef = useRef<number | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);

  if (enabled && startedAtRef.current === null) startedAtRef.current = Date.now();

  const handleDone = (
    key: string,
    dataUrl: string | null,
    legendRows: LegendRow[],
    weatherPanel: WeatherPanel | null
  ) => {
    setCaptures((prev) => ({ ...prev, [key]: dataUrl }));
    const sceneId = key.split("::")[0];
    if (weatherPanel) {
      setWeatherPanels((prev) => (prev[sceneId] ? prev : { ...prev, [sceneId]: weatherPanel }));
    }
    setLegends((prev) => ({
      ...prev,
      [sceneId]: mergeLegendRows(prev[sceneId] ?? [], legendRows),
    }));
    setElapsedMs(Date.now() - (startedAtRef.current ?? Date.now()));
    setIndex((i) => i + 1);
  };

  const done = enabled && (cancelled || index >= jobs.length);
  const job = enabled && !done ? jobs[index] : null;

  const remaining = Math.max(0, jobs.length - index);
  const secondsRemaining =
    index > 0 && remaining > 0
      ? Math.round(((elapsedMs / index) * remaining) / 1000)
      : null;

  return {
    captures,
    legends,
    weatherPanels,
    current: Math.min(index + 1, Math.max(jobs.length, 1)),
    total: jobs.length,
    done,
    secondsRemaining,
    cancelled,
    cancel: () => setCancelled(true),
    runner: job ? (
      <CaptureRunner
        key={job.key}
        job={job}
        sources={sources}
        isPublic={isPublic}
        onDone={handleDone}
      />
    ) : null,
  };
}
